import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
  IconButton,
  Box,
  Grid,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth, Branch } from '../../../contexts/AuthContext';
import { mainApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface MenuToEdit {
  id: string;
  name: string;
  branchId?: string | null;
  branchName?: string;
}

interface AddQRMenuModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  // ✅ Düzenleme modu prop'ları
  editMode?: boolean;
  menuToEdit?: MenuToEdit | null;
  createMode?: 'qr' | 'ticket';
}

const AddQRMenuModal: React.FC<AddQRMenuModalProps> = ({ 
  open, 
  onClose, 
  companyId,
  editMode = false,
  menuToEdit = null,
  createMode = 'qr'
}) => {
  const { branches, loadBranches } = useAuth(); // ✅ AuthContext'ten branches ve loadBranches alınıyor
  const { t } = useTranslation('salesSystem');
  const [menuName, setMenuName] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const isTicketMode = createMode === 'ticket';
  const entityLabel = isTicketMode
    ? t('qrMenuAddModal.entity.onlineTicketPage')
    : t('qrMenuAddModal.entity.qrMenu');

  // Modal açıldığında şubeleri yeniden yükle (forceRefresh ile)
  useEffect(() => {
    if (open) {
      console.log('🔄 [AddQRMenuModal] Modal açıldı, şubeler yeniden yükleniyor...');
      setLoadingBranches(true);
      loadBranches(true).finally(() => {
        setLoadingBranches(false);
        console.log('✅ [AddQRMenuModal] Şubeler yenilendi');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // loadBranches dependency'den çıkarıldı - sonsuz döngü önlendi

  // Modal açıldığında/kapandığında state'leri ayarla
  useEffect(() => {
    if (!open) {
      setMenuName('');
      setSelectedBranch(null);
      setError(null);
    } else if (editMode && menuToEdit) {
      // ✅ Düzenleme modunda mevcut değerleri yükle
      setMenuName(menuToEdit.name || '');
      // Şube bilgisi varsa seç
      if (menuToEdit.branchId && branches.length > 0) {
        const branch = branches.find(b => b.id === menuToEdit.branchId);
        setSelectedBranch(branch || null);
      }
    }
  }, [open, editMode, menuToEdit, branches]);

  const handleSave = async () => {
    if (!menuName || !companyId) {
      setError(
        t('qrMenuAddModal.errors.nameRequired', {
          field: isTicketMode
            ? t('qrMenuAddModal.fields.pageName')
            : t('qrMenuAddModal.fields.menuName')
        })
      );
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // ✅ Düzenleme modunda güncelleme yap
      // ⚠️ CRITICAL_API_DEVELOPMENT_RULES.md'ye göre:
      // - PUT değil POST kullanılır
      // - Request body'de PascalCase kullanılır
      if (editMode && menuToEdit) {
        console.log(`🔄 QR menü güncelleniyor: ${menuName} (ID: ${menuToEdit.id}, Şirket: ${companyId})`);
        
        const updateData = {
          Name: menuName,
          BranchId: selectedBranch?.id || null,
          BranchName: selectedBranch?.name || null
        };
        
        await mainApi.post(`/api/QRMenu/menu-definitions/${menuToEdit.id}/update`, updateData);
        
        console.log(`✅ QR Menü güncellendi: ${menuName}`);
        onClose();
        return;
      }
      
      // Yeni menü oluşturma
      const newMenuId = `menu_${Date.now()}`;
      
      console.log(`🔄 Yeni QR menü SQL'e kaydediliyor: ${menuName} (ID: ${newMenuId}, Şirket: ${companyId})`);
      
      const menuDefinitionData = {
        firebaseId: newMenuId,
        name: menuName,
        description: null,
        branchId: selectedBranch?.id || null,
        branchName: selectedBranch?.name || null,
        createdAt: new Date().toISOString(),
        productIds: null,
        template: isTicketMode ? 'ticket' : 'standard',
        subMenus: null,
        settings: JSON.stringify({
          mode: isTicketMode ? 'ticket' : 'qr',
          appearance: {
            template: 'standard',
            primaryColor: '#1976d2',
            fontFamily: 'Roboto',
            showPrices: true,
            showImages: true,
            showDescriptions: true,
            showLogo: true
          }
        })
      };
      
      const response = await mainApi.post(`/api/QRMenu/menu-definitions`, menuDefinitionData);
      console.log(`✅ Yeni QR Menü SQL'e kaydedildi: ${menuName}`, response.data);
      handleClose();
    } catch (err) {
      console.error("❌ QR Menü kaydetme API hatası:", err);
      setError(t('qrMenuAddModal.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Modal kapatılırken state'leri sıfırla
  const handleClose = () => {
    setMenuName('');
    setSelectedBranch(null);
    setError(null);
    setSaving(false);
    onClose(); // Prop'tan gelen kapatma fonksiyonunu çağır
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QrCode2Icon sx={{ mr: 1 }} />
          {editMode
            ? t('qrMenuAddModal.title.edit', { entity: entityLabel })
            : t('qrMenuAddModal.title.create', { entity: entityLabel })}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Hata Mesajı Alanı */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)} // Hata mesajını kapatma butonu
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          {/* Menü/Sayfa Adı */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={isTicketMode ? t('qrMenuAddModal.fields.pageName') : t('qrMenuAddModal.fields.menuName')}
              value={menuName}
              onChange={(e) => setMenuName(e.target.value)}
              required
              InputProps={{
                startAdornment: <RestaurantMenuIcon color="action" sx={{ mr: 1 }} />
              }}
              helperText={
                isTicketMode
                  ? t('qrMenuAddModal.fields.pageNameHelp')
                  : t('qrMenuAddModal.fields.menuNameHelp')
              }
            />
          </Grid>

          {/* Atanan Şube */}
          <Grid item xs={12}>
            <Autocomplete
              options={branches.filter(b => b.id !== "null")} // "Şubeye Ait Değil" seçeneğini hariç tut
              getOptionLabel={(option) => option.name}
              value={selectedBranch}
              onChange={(_, newValue) => setSelectedBranch(newValue)}
              loading={loadingBranches}
              disabled={loadingBranches}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('qrMenuAddModal.fields.assignedBranch')}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <BusinessIcon color="action" sx={{ mr: 1 }} />,
                    endAdornment: (
                      <>
                        {loadingBranches ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  helperText={loadingBranches ? t('qrMenuAddModal.fields.loadingBranches') : undefined}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          {t('qrMenuAddModal.actions.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!menuName || saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving
            ? t('qrMenuAddModal.actions.saving')
            : (editMode ? t('qrMenuAddModal.actions.update') : t('qrMenuAddModal.actions.create'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddQRMenuModal; 